import { App } from "antd";
import { CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled, InfoCircleFilled } from "@ant-design/icons";
import { isValidElement, useMemo } from "react";
import type { ArgsProps, MessageInstance, NoticeType, TypeOpen } from "antd/es/message/interface";

const adapters = new WeakMap<MessageInstance, MessageInstance>();
const icons = { success: CheckCircleFilled, error: CloseCircleFilled, warning: ExclamationCircleFilled, info: InfoCircleFilled };

export const feedbackAlertIcons = {
  successIcon: <span aria-hidden="true"><CheckCircleFilled /></span>,
  errorIcon: <span aria-hidden="true"><CloseCircleFilled /></span>,
  warningIcon: <span aria-hidden="true"><ExclamationCircleFilled /></span>,
  infoIcon: <span aria-hidden="true"><InfoCircleFilled /></span>,
};

// Reuse Message's lifecycle. A state icon is never a dismiss control.
export function createFeedbackMessage(api: MessageInstance): MessageInstance {
  const existing = adapters.get(api);
  if (existing) return existing;
  let lastAnchor: HTMLElement | null = null;
  let sequence = 0;
  let retireCurrent: (() => void) | undefined;
  const open = (args: ArgsProps) => {
    if (args.type === "loading") return api.open(args);
    if (document.activeElement instanceof HTMLElement && !document.activeElement.closest(".operation-feedback")) lastAnchor = document.activeElement;
    const anchor = lastAnchor;
    const keepFocus = document.activeElement instanceof HTMLElement && Boolean(document.activeElement.closest(".operation-feedback"));
    retireCurrent?.();
    const key = `operation-feedback-${++sequence}`;
    let dismissed = false;
    let hovered = false;
    let region: HTMLSpanElement | null = null;
    const restoreFocus = () => {
      if (region?.contains(document.activeElement)) {
        const target = anchor?.isConnected && !anchor.closest(".operation-feedback") ? anchor : document.querySelector<HTMLElement>("main h1, main");
        target?.focus({ preventScroll: true });
      }
    };
    const hide = () => {
      const notice = region?.closest<HTMLElement>(".ant-message-notice");
      if (notice) { notice.setAttribute("aria-hidden", "true"); notice.inert = true; }
    };
    const close = () => { dismissed = true; restoreFocus(); hide(); api.destroy(key); };
    retireCurrent = () => { dismissed = true; hide(); api.destroy(key); };
    const Icon = icons[args.type as keyof typeof icons] ?? InfoCircleFilled;
    const config: ArgsProps = {
      ...args, key, duration: 5, pauseOnHover: false,
      icon: <span aria-hidden="true"><Icon /></span>,
      onClose: () => { dismissed = true; restoreFocus(); hide(); args.onClose?.(); },
      content: <span ref={node => { region = node; if (node && keepFocus && !node.contains(document.activeElement)) node.querySelector<HTMLButtonElement>("button")?.focus(); }} className="operation-feedback"
        onMouseEnter={() => { hovered = true; void api.open({ ...config, duration: Infinity }); }}
        onMouseLeave={() => { hovered = false; if (!dismissed && !region?.contains(document.activeElement)) void api.open(config); }}
        onFocusCapture={() => { void api.open({ ...config, duration: Infinity }); }}
        onBlurCapture={event => { if (!dismissed && !hovered && !event.currentTarget.contains(event.relatedTarget)) void api.open(config); }}
        onKeyDown={event => { if (event.key === "Escape") { event.stopPropagation(); close(); } }}>
        <span>{args.content}</span>
        <button type="button" aria-label="关闭提示" className="operation-feedback-close" onClick={event => { event.stopPropagation(); close(); }}><span aria-hidden="true">×</span></button>
      </span>,
    };
    return api.open(config);
  };
  const method = (type: NoticeType): TypeOpen => (content, duration, onClose) => {
    const args: ArgsProps = content !== null && typeof content === "object" && !isValidElement(content) && "content" in content ? content : { content };
    return open({ ...args, type, onClose: typeof duration === "function" ? duration : onClose ?? args.onClose });
  };
  const adapted = { ...api, open, success: method("success"), error: method("error"), warning: method("warning"), info: method("info") };
  adapters.set(api, adapted);
  return adapted;
}

export function useFeedbackApp() {
  const app = App.useApp();
  const message = useMemo(() => createFeedbackMessage(app.message), [app.message]);
  return { ...app, message };
}
